import { ReactComponent as AdvertisersIcon } from "@/assets/dashboard/side-nav/Advertisers.svg";
import { ReactComponent as CampaignsIcon } from "@/assets/dashboard/side-nav/Campaigns.svg";
import { ReactComponent as DashboardIcon } from "@/assets/dashboard/side-nav/Dashboard.svg";
import { ReactComponent as LibraryIcon } from "@/assets/dashboard/side-nav/Library.svg";
import { ReactComponent as MatchedAudiencesIcon } from "@/assets/dashboard/side-nav/Matched Audiences.svg";
import { ReactComponent as SetupIcon } from "@/assets/dashboard/side-nav/Setup.svg";

import { ReactComponent as AssetsIcon } from "@/assets/dashboard/side-nav/Assets.svg";
import { ReactComponent as IntegrationsIcon } from "@/assets/dashboard/side-nav/Integrations.svg";
import { ReactComponent as ReportsIcon } from "@/assets/dashboard/side-nav/Reports.svg";
import { ReactComponent as SiteVisitorIcon } from "@/assets/dashboard/side-nav/SiteVisitor.svg";
import { ReactComponent as UsersIcon } from "@/assets/dashboard/side-nav/Users.svg";
import { IS_BLUEWHALE, MODULES } from "@/constants";
import { IPermissions } from "@/modules/User/user.interface";
import routes from "@/routes";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LineItemsNavIcon from "./LineItemsNavIcon";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import ListIcon from "@mui/icons-material/List";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import SourceIcon from "@mui/icons-material/Source";
import { ISpecificCompanyIdTabs } from "./drawer.interface";

export const TABS = {
  DASHBOARD: { label: "Dashboard", path: "", icon: DashboardIcon },
  INTEGRATIONS: {
    label: "Integrations",
    path: routes.INTEGRATIONS,
    icon: IntegrationsIcon,
  },
  ADVERTISERS: {
    label: "Advertisers",
    path: routes.ADVERTISERS.INDEX,
    icon: AdvertisersIcon,
  },
  CAMPAIGNS: {
    label: "Campaigns",
    path: routes.CAMPAIGNS.INDEX,
    icon: CampaignsIcon,
    children: [
      {
        label: "General Campaign",
        path: routes.CAMPAIGNS.GENERAL_CAMPAIGN.INDEX,
        icon: IntegrationsIcon,
        key: "CAMPAIGNS",
      },
      {
        label: "Quick Campaign",
        path: routes.CAMPAIGNS.QUICK_CAMPAIGN.INDEX,
        icon: IntegrationsIcon,
        key: "QUICK_CAMPAIGNS",
      },
    ],
  },
  LINE_ITEMS: {
    label: "Line Items",
    path: routes.LINE_ITEMS.INDEX,
    icon: LineItemsNavIcon,
  },
  MATCHED_AUDIENCE: {
    label: "Matched Audiences",
    path: routes.AUDIENCES.INDEX,
    icon: MatchedAudiencesIcon,
    children: [
      {
        label: "Account Audience",
        path: routes.AUDIENCES.ACCOUNT_AUDIENCE.INDEX,
        icon: MatchedAudiencesIcon,
        key: MODULES.AUDIENCE_ACCOUNT,
      },
      {
        label: "Contact Audience",
        path: routes.AUDIENCES.EMAIL_AUDIENCES.INDEX,
        icon: MatchedAudiencesIcon,
        key: MODULES.AUDIENCE_EMAIL,
      },
      {
        label: "Retargeting Audience",
        path: routes.AUDIENCES.RETARGETING.INDEX,
        icon: MatchedAudiencesIcon,
        key: MODULES.AUDIENCE_SITE_VISITOR,
      },
      {
        label: "Audience Manager",
        path: routes.AUDIENCE_MANAGER.INDEX,
        icon: MatchedAudiencesIcon,
        key: MODULES.AUDIENCE_MANAGER,
      },
    ],
  },
  SETUP: {
    label: "Setup",
    path: routes.REPORTS.REPORTS_SETUP.INDEX,
    icon: SetupIcon,
  },
  ASSETS: {
    label: "Assets",
    icon: AssetsIcon,
    path: undefined,
    children: [
      {
        label: "Creatives",
        path: routes.ASSETS.CREATIVES.INDEX,
        icon: IntegrationsIcon,
        key: "CREATIVES",
      },
      {
        label: "Native Offers",
        path: routes.ASSETS.NATIVE_OFFERS.INDEX,
        icon: IntegrationsIcon,
        key: "NATIVE_OFFERS",
      },
      {
        label: "Templates",
        path: routes.ASSETS.TEMPLATE.INDEX,
        icon: IntegrationsIcon,
        key: "CREATIVE_TEMPLATES",
      },
      {
        label: "Forms",
        path: routes.ASSETS.LEADGEN.INDEX,
        icon: LibraryIcon,
        key: "FORMS",
      },
      { label: "UTMs", path: routes.UTM.INDEX, icon: LibraryIcon, key: "UTMS" },
    ],
  },
  TAG_MANAGER: {
    label: "Tag Manager",
    icon: SiteVisitorIcon,
    path: undefined,
    children: [
      {
        label: "Tag",
        path: routes.SITE_VISITER.INDEX,
        icon: IntegrationsIcon,
        key: "TAGS",
      },
      {
        label: "Conversions",
        path: routes.CONVERSION.INDEX,
        icon: LibraryIcon,
        key: "CONVERSIONS",
      },
    ],
  },
  LINKEDIN_ENGAGEMANT: {
    label: "Linkedin Engagemant",
    icon: SiteVisitorIcon,
    path: routes.LINKEDIN_ENGAGEMANT.INDEX,
  },
  VISITOR_INSIGHT: {
    label: "Visitor Insights",
    path: routes.VISITOR_INSIGHTS.INDEX,
    icon: QueryStatsIcon,
    children: [
      /*
        Two entries, both graduated. The first-generation Company Insights and
        Profile Insights and the Keyword Settings page are gone from here and
        from the route table; only these two remain, and they drop "(Beta)".

        The nav SHAPE changes as a result, and it is worth knowing before
        someone reports it as a bug. `extractSingleChildToRoot` collapses a tab
        into a flat link when exactly one child survives the permission filter.
        A user holding only COMPANY_VISITOR_INSIGHTS had three children and now
        has one; a PROFILE-only user had two and now has one. Both drop from an
        expandable "Visitor Insights" group to a direct link. Nobody loses
        reach — `getFirstTabPath` reads the collapsed form's `path` — but the
        group stops expanding for everyone who holds a single key.
      */
      {
        label: "Company Insights",
        path: routes.VISITOR_INSIGHTS.COMPANY_BETA.INDEX,
        icon: IntegrationsIcon,
        key: "COMPANY_VISITOR_INSIGHTS",
      },
      {
        label: "Profile Insights",
        path: routes.VISITOR_INSIGHTS.PROFILE_BETA.INDEX,
        icon: IntegrationsIcon,
        key: "PROFILE_VISITOR_INSIGHTS",
      },
    ],
  },

  REPORTS: {
    label: "Reports",
    path: "reports",
    icon: ReportsIcon,
    children: [
      {
        label: "ABM",
        path: routes.REPORTS.ABM_BETA.INDEX,
        icon: ReportsIcon,
        key: MODULES.ABM_STATS,
      },
      {
        label: "CBM",
        path: routes.REPORTS.CBM_BETA.INDEX,
        icon: ReportsIcon,
        key: MODULES.ABM_STATS,
      },
      // LEGACY (2026-10-14): the old ABM page (modules/Report/AbmNew) is shown
      // only on BLUEWHALERESEARCH, as "ABM (Legacy)", after CBM. Build-time
      // brand, so the array is fixed at module load.
      ...(IS_BLUEWHALE
        ? [
            {
              label: "ABM (Legacy)",
              path: routes.REPORTS.ABM.INDEX,
              icon: ReportsIcon,
              key: MODULES.ABM_STATS,
            },
          ]
        : []),
      // LEGACY (2026-10-14): old Campaign Stats (routes.REPORTS.STATS) is
      // hidden on every brand, Bluewhale included; superseded by the entry below.
      // {
      //   label: "Campaign Stats",
      //   path: routes.REPORTS.STATS.INDEX,
      //   icon: ReportsIcon,
      //   key: MODULES.CAMPAIGN_STATS,
      // },
      {
        label: "Campaign Stats",
        path: routes.REPORTS.STATS_BETA.INDEX,
        icon: ReportsIcon,
        key: MODULES.CAMPAIGN_STATS,
      },
      {
        label: "Reports Setup",
        path: routes.REPORTS.REPORTS_SETUP.INDEX,
        icon: ReportsIcon,
        key: MODULES.REPORTS_SETUP,
      },
      // RETIRED (2026-10-14): Attribution module retired — not granted, hidden for everyone
      // {
      //   label: "Attribution",
      //   path: routes.REPORTS.ATTRIBUTION.INDEX,
      //   icon: ReportsIcon,
      //   key: MODULES.ATTRIBUTION,
      // },
    ],
  },
  CONTACT_BI: {
    label: "Contact BI",
    path: routes.CONTACT.INDEX,
    icon: AlternateEmailIcon,
  },
  USERS: { label: "Users", path: "users", icon: UsersIcon },
  PRESETS: { label: "Presets", path: routes.PRESET.INDEX, icon: SourceIcon },
  CUSTOM_LISTS: {
    label: "Custom Lists",
    path: routes.CUSTOM_LISTS.INDEX,
    icon: ListIcon,
  },
  ONBOARDING_CONNECTIONS: {
    label: "Onboarding connections",
    path: routes.ONBOARDING_DISTRIBUTION_ADMIN.INDEX,
    icon: AdminPanelSettingsOutlinedIcon,
  },
};

export function permittedTabs(permission: IPermissions, isAdmin: boolean) {
  if (isAdmin) {
    return SUPER_ADMIN_TABS;
  }
  const tabsValue = Object.values(TABS);

  const drawerList: typeof tabsValue = [];
  if (permission[MODULES.DASHBOARD]) {
    drawerList.push(checkSubTab(TABS.DASHBOARD, permission));
  }
  if (
    permission[MODULES.INTEGRATIONS_BEESWAX] ||
    permission[MODULES.INTEGRATIONS_FACEBOOK] ||
    permission[MODULES.INTEGRATIONS_LINKEDIN] ||
    permission[MODULES.INTEGRATIONS_HEYREACH] ||
    permission[MODULES.INTEGRATIONS_SMARTLEAD] ||
    permission[MODULES.INTEGRATIONS_DISPLAYVIDEO360] ||
    permission[MODULES.INTEGRATIONS_TRADEDESK] ||
    permission[MODULES.INTEGRATIONS_SALESFORCE] ||
    permission[MODULES.INTEGRATIONS_LIVERAMP] ||
    permission[MODULES.INTEGRATIONS_HUBSPOT] ||
    permission[MODULES.INTEGRATIONS_CLAY]
  ) {
    drawerList.push(checkSubTab(TABS.INTEGRATIONS, permission));
  }
  if (permission[MODULES.ADVERTISERS]) {
    drawerList.push(checkSubTab(TABS.ADVERTISERS, permission));
  }
  if (permission[MODULES.CAMPAIGNS] || permission[MODULES.QUICK_CAMPAIGNS]) {
    drawerList.push(checkSubTab(TABS.CAMPAIGNS, permission));
  }
  // CAMPAIGNS only — the row-click edit route is CAMPAIGNS-gated (see main.routes).
  if (permission[MODULES.CAMPAIGNS]) {
    drawerList.push(checkSubTab(TABS.LINE_ITEMS, permission));
  }
  if (
    permission[MODULES.AUDIENCE_ACCOUNT] ||
    permission[MODULES.AUDIENCE_EMAIL] ||
    permission[MODULES.AUDIENCE_SITE_VISITOR] ||
    permission[MODULES.AUDIENCE_MANAGER]
  ) {
    drawerList.push(checkSubTab(TABS.MATCHED_AUDIENCE, permission));
  }
  if (
    permission[MODULES.CREATIVES] ||
    permission[MODULES.CREATIVE_TEMPLATES] ||
    permission[MODULES.FORMS] ||
    permission[MODULES.NATIVE_OFFERS] ||
    permission[MODULES.UTMS]
  ) {
    drawerList.push(checkSubTab(TABS.ASSETS, permission));
  }

  if (permission[MODULES.TAGS] || permission[MODULES.CONVERSIONS]) {
    drawerList.push(checkSubTab(TABS.TAG_MANAGER, permission));
  }
  if (permission[MODULES.LINKEDIN_ENGAGEMANT]) {
    drawerList.push(checkSubTab(TABS.LINKEDIN_ENGAGEMANT, permission));
  }

  if (
    permission[MODULES.COMPANY_VISITOR_INSIGHTS] ||
    permission[MODULES.PROFILE_VISITOR_INSIGHTS]
  ) {
    drawerList.push(checkSubTab(TABS.VISITOR_INSIGHT, permission));
  }
  if (permission[MODULES.CONTACT_BI]) {
    drawerList.push(checkSubTab(TABS.CONTACT_BI, permission));
  }
  if (
    permission[MODULES.CAMPAIGN_STATS] ||
    permission[MODULES.ABM_STATS] ||
    permission[MODULES.REPORTS_SETUP]
  ) {
    drawerList.push(checkSubTab(TABS.REPORTS, permission));
  }
  if (
    permission[MODULES.AGENCY_USERS] ||
    permission[MODULES.ADVERTISER_USERS]
  ) {
    drawerList.push(checkSubTab(TABS.USERS, permission));
  }
  if (permission[MODULES.TARGETING_EXPRESSION_PRESET]) {
    drawerList.push(checkSubTab(TABS.PRESETS, permission));
    drawerList.push(checkSubTab(TABS.CUSTOM_LISTS, permission));
  }

  return drawerList;
}

/**
 * Path (relative to /dashboard) of the first sidebar tab the user may open.
 * A user with no permitted tab (e.g. a role holding only retired keys) has no
 * menu at all; send them to Account Info, which every non-admin route set
 * mounts, instead of crashing on `tabs[0]`. Callers: Login, Register,
 * GoogleSignup, CompleteProfile, UserContext (login-as user / advertiser).
 */
export function getFirstTabPath(
  tabs: ReturnType<typeof permittedTabs> | null | undefined
): string {
  const first = tabs?.[0];
  return first?.path ?? first?.children?.[0]?.path ?? routes.ACCOUNT_INFO;
}

function checkSubTab(tab: any, permission: any) {
  if (tab.children) {
    const tabs: any = [];
    tab.children.forEach((child: any) => {
      if (permission[child.key]) tabs.push(child);
    });
    tab.children = tabs;
  }
  return extractSingleChildToRoot(tab);
}

function extractSingleChildToRoot(tab: any) {
  if (tab?.children?.length === 1) {
    tab.path = [tab.path, tab.children[0].path]
      .filter((item) => item)
      .join("/");
    delete tab.children;
  }
  return tab;
}

export const SUPER_ADMIN_TABS = [
  TABS.ADVERTISERS,
  TABS.MATCHED_AUDIENCE,
  TABS.SETUP,
  TABS.ASSETS,
  TABS.TAG_MANAGER,
  TABS.LINKEDIN_ENGAGEMANT,
  TABS.ONBOARDING_CONNECTIONS,
];
export const AGENCY_ADMIN_TABS = [
  TABS.DASHBOARD,
  TABS.INTEGRATIONS,
  TABS.ADVERTISERS,
  TABS.CAMPAIGNS,
  TABS.MATCHED_AUDIENCE,
  TABS.ASSETS,
  TABS.TAG_MANAGER,
  TABS.VISITOR_INSIGHT,
  TABS.REPORTS,
  TABS.CONTACT_BI,
  TABS.USERS,
];
export const AGENCY_USER_TABS = [
  TABS.DASHBOARD,
  TABS.ADVERTISERS,
  TABS.CAMPAIGNS,
  TABS.MATCHED_AUDIENCE,
  TABS.ASSETS,
  TABS.TAG_MANAGER,
  TABS.VISITOR_INSIGHT,
  TABS.REPORTS,
  TABS.CONTACT_BI,
  TABS.USERS,
];
export const ADVERTISER_TABS = [
  TABS.DASHBOARD,
  TABS.INTEGRATIONS,
  TABS.CAMPAIGNS,
  TABS.MATCHED_AUDIENCE,
  TABS.ASSETS,
  TABS.TAG_MANAGER,
  TABS.VISITOR_INSIGHT,
  TABS.REPORTS,
  TABS.CONTACT_BI,
  TABS.USERS,
];
export const SPECIFIC_COMPANY_ID_TABS: ISpecificCompanyIdTabs = {
  206: [TABS.INTEGRATIONS, TABS.ADVERTISERS, TABS.MATCHED_AUDIENCE, TABS.USERS],
  211: [TABS.INTEGRATIONS, TABS.ADVERTISERS, TABS.MATCHED_AUDIENCE, TABS.USERS],
};
